import { Button, Checkbox, Grid, NumberInput, Select, Text } from '@mantine/core';
import { useForm } from '@mantine/form';
import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { requiredFieldFormValidator } from '../../utils/validators/formValidators';
import { DEFAULT_FOLIO_PREFIX } from '../../constants';
import { useSelector } from 'react-redux';

type ReduxState = {
  user: {
    company?: {
      companyServicesPermissions?: {
        enableOutsourcing?: boolean;
      };
    };
  };
};

interface UpdateOption {
  id: string;
  label: string;
  onSubmit: ({
    sequence,
    storeId,
    params
  }: {
    sequence: string;
    storeId: string;
    params: {
      markOutsourcedAsReturned?: boolean;
    };
  }) => unknown | Promise<unknown>;
}

export default memo(function UpdateOrderForm({
  updateOptions = [],
  close = () => {},
  storeId = '',
  orderFolioPrefix = DEFAULT_FOLIO_PREFIX
}: {
  updateOptions: UpdateOption[];
  close: () => void;
  storeId: string;
  orderFolioPrefix?: string | null;
}) {
  const { t } = useTranslation([
    'FieldName',
    'CommonButtons',
    'FormValidator',
    'Backend',
    'Notifications'
  ]);
  const company = useSelector((state: ReduxState) => state.user.company);
  const prefix = orderFolioPrefix || DEFAULT_FOLIO_PREFIX;
  const outsourcingEnabled = Boolean(company?.companyServicesPermissions?.enableOutsourcing);
  const [loading, setLoading] = useState(false);
  const form = useForm({
    mode: 'uncontrolled',
    initialValues: {
      sequence: '' as string | number,
      type: updateOptions.length ? updateOptions[0].id : '',
      markOutsourcedAsReturned: outsourcingEnabled
    },
    validate: {
      sequence: (value) => {
        const requiredValidatorRes = requiredFieldFormValidator(value);
        if (requiredValidatorRes) return requiredValidatorRes;
      },
      type: (value) => {
        const requiredValidatorRes = requiredFieldFormValidator(value);
        if (requiredValidatorRes) return requiredValidatorRes;
      }
    }
  });
  type FormValues = typeof form.values;

  const submit = async (values: FormValues) => {
    try {
      setLoading(true);
      const updateOption = updateOptions.find((updateOption) => updateOption.id === values.type);
      const result = await updateOption?.onSubmit({
        sequence: values.sequence.toString(),
        storeId: storeId,
        params: {
          markOutsourcedAsReturned: values.markOutsourcedAsReturned
        }
      });
      if (result !== false) close();
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <form onSubmit={form.onSubmit(submit)}>
        <Grid align="center">
          {form.getValues().type === 'markAsCleaned' && outsourcingEnabled && (
            <Grid.Col span={12}>
              <Checkbox
                {...form.getInputProps('markOutsourcedAsReturned', { type: 'checkbox' })}
                label={t('Notifications:CRUD.markOutsourcedReturned')}
              />
            </Grid.Col>
          )}
          <Grid.Col span={12}>
            <NumberInput
              {...form.getInputProps('sequence')}
              leftSectionWidth={Math.min(Math.max((prefix.length + 1) * 8 + 12, 36), 144)}
              leftSection={
                <Text
                  size="sm"
                  truncate
                  title={`${prefix}-`}
                  style={{ whiteSpace: 'nowrap', maxWidth: '100%', paddingInline: 6 }}
                >
                  {prefix}-
                </Text>
              }
              allowDecimal={false}
              allowLeadingZeros={false}
              placeholder={t('FieldName:order')}
            />
          </Grid.Col>
          <Grid.Col span={12}>
            <Select
              {...form.getInputProps('type')}
              allowDeselect={false}
              placeholder={t('FieldName:type')}
              data={updateOptions.map((updateOption) => {
                return {
                  label: updateOption.label,
                  value: updateOption.id
                };
              })}
            />
          </Grid.Col>
          <Grid.Col span={12}>
            <Button fullWidth type="submit" loading={loading}>
              {t('CommonButtons:submit')}
            </Button>
          </Grid.Col>
        </Grid>
      </form>
    </>
  );
});
